import Add from '@mui/icons-material/Add';
import Clear from '@mui/icons-material/Clear';
import Edit from '@mui/icons-material/Edit';
import Mail from '@mui/icons-material/Mail';
import MoreHoriz from '@mui/icons-material/MoreHoriz';
import Sms from '@mui/icons-material/Sms';
import ToggleOffOutlined from '@mui/icons-material/ToggleOffOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import {
  CompanyShareableType,
  CompliantType,
  deleteV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersId,
  getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdDrivers,
  ModelError_Item,
  ModelError_Response,
  patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdAccountsId,
  patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesId,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { startCase } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';

import EquipmentManagementColumn from '~components/DataGrid/columns/EquipmentManagementColumn';
import DataGridPager from '~components/DataGrid/DataGridPager';
import actionsColumn from '~components/DataTable/actionsColumn';
import DataTable from '~components/DataTable/DataTable';
import { DataTableColumn } from '~components/DataTable/dataTableColumn';
import DeactivateConfirmationDialog from '~components/DeactivateConfirmationDialog/DeactivateConfirmationDialog';
import { DriverCreateDialog } from '~components/Driver/DriverCreateDialog';
import { DriverEditDialog } from '~components/Driver/DriverEditDialog';
import { DriverFormValues } from '~components/DriverForm/DriverForm';
import { SimpleMenu } from '~components/Menu/SimpleMenu';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import { UserValidationDTOWithSitesDiff } from '~components/UserForm/v2/UserForm';
import { InviteType } from '~components/UserInvite/UserInviteTypeSelector';
import { MAX_API_PAGE_SIZE } from '~constants/consts';
import { Company } from '~hooks/useCompany/models';
import { CompanyShare } from '~hooks/useCompanyShares/models';
import { useCompanyShares } from '~hooks/useCompanyShares/useCompanyShares';
import { StagedComplianceDoc } from '~hooks/useComplianceDocuments/checklist';
import { useCreateStagedComplianceDocuments } from '~hooks/useComplianceDocuments/useCreateStagedComplianceDocuments';
import useCursorPagination from '~hooks/useCursorPagination/useCursorPagination';
import { Driver } from '~hooks/useDrivers/models';
import { useDrivers } from '~hooks/useDrivers/useDrivers';
import { BasicEquipmentDefault, EquipmentTypeahead } from '~hooks/useEquipment/models';
import { User } from '~hooks/useUsers/models';
import useUsers from '~hooks/useUsers/useUsers';
import { EquipmentSelector } from '~pages/Settings/UserManagement/EquipmentSelector';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { Nullable } from '~types/Nullable';
import { dateFormat } from '~utils/dateTime';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';
import { formatPhoneForDisplay } from '~utils/utilFunctions';

interface ManagedVendorDriversTabProps {
  managedCompanyId: string;
  managedCompany: Company;
}

export const ManagedVendorDriversTab = observer(
  ({ managedCompanyId, managedCompany }: ManagedVendorDriversTabProps) => {
    const { getDriversByCompanyId, isLoading, createDriverByCompanyId, getDriverById } =
      useDrivers();
    const { userStore, toasterStore } = useStores();
    const [searchQuery, setSearchQuery] = useState('');
    const [asyncErrors, setAsyncErrors] = useState<ModelError_Item[]>([]);
    const { updateUser, isUpdating, sendInvitation } = useUsers();
    const {
      updateCompanyShare,
      createCompanyShareByCompanyId,
      deleteCompanyShare,
      getCompanySharesByCompanyId,
    } = useCompanyShares();
    const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
    const [selectedDriver, setSelectedDriver] = useState<Driver | undefined>();
    const [companyShares, setCompanyShares] = useState<CompanyShare[]>([]);
    const [isDriverDialogOpen, setIsDriverDialogOpen] = useState(false);
    const [userIdToDelete, setUserIdToDelete] = useState<string>();
    const [stagedComplianceDocs, setStagedComplianceDocs] = useState<
      StagedComplianceDoc[]
    >([]);
    const { createStagedDocs } = useCreateStagedComplianceDocuments(managedCompanyId);
    const userPermissions = useMemo(() => {
      return userStore.getPermissions();
    }, [userStore]);

    const companyId = getEffectiveUserCompanyId(userStore);
    const showDriverTrailerSettingEnabled =
      userStore.companySettings.isEnabled('show_driver_trailer');

    const {
      goToPage: goToDriversPage,
      isFetching: isFetchingDrivers,
      pagination: driversPagination,
      reloadCurrentPage: reloadDriversPage,
      rows: drivers,
      startOver: startDriversOver,
    } = useCursorPagination<Driver>({
      fetchPage: (params) =>
        getDriversByCompanyId({ companyId, managedCompanyId, searchQuery, ...params }),
      limit: MAX_API_PAGE_SIZE,
    });

    useEffect(() => {
      if (managedCompanyId) {
        void startDriversOver();
      }
    }, [companyId, managedCompanyId, searchQuery]);

    const handleCloseDriverDialog = () => {
      setIsDriverDialogOpen(false);
      setAsyncErrors([]);
    };

    const handleOpenDriverDialog = () => {
      setAsyncErrors([]);
      setIsDriverDialogOpen(true);
    };

    const handleCloseEditDialog = () => {
      setIsEditDialogOpen(false);
      setSelectedDriver(undefined);
      setCompanyShares([]);
      setAsyncErrors([]);
    };

    const handleEditDriver = async (id: string) => {
      try {
        const driver = await getDriverById(id, companyId, managedCompanyId);
        setSelectedDriver(driver as Driver);

        // Fetch company shares for the driver
        if (driver) {
          await getCompanySharesByCompanyId({
            companyId,
            managedCompanyId,
            company_shareable_type: CompanyShareableType.USER,
            company_shareable_id: id,
            sender_company_id: managedCompanyId,
            callBack: setCompanyShares,
          });
        }

        setIsEditDialogOpen(true);
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.drivers.failed_to_fetch_driver'),
        );
      }
    };

    const onDriverSubmitCallback = async (
      data: DriverFormValues,
      inviteType: InviteType[],
    ) => {
      const shouldSendEmailInvite = inviteType.includes('email');
      const shouldSendSmsInvite = inviteType.includes('sms');
      const invitesToSend = { email: shouldSendEmailInvite, sms: shouldSendSmsInvite };

      try {
        const newAccount = await createDriverByCompanyId(
          data,
          invitesToSend,
          managedCompanyId,
          managedCompany.address,
        );

        if (newAccount?.id && stagedComplianceDocs.length > 0) {
          const connectedCompanyId = newAccount.connectedCompany?.id;
          if (connectedCompanyId) {
            try {
              const response =
                await getV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdDrivers({
                  path: {
                    'company-id': companyId,
                    'managed-company-id': connectedCompanyId,
                  },
                  query: { 'page[limit]': 1 },
                });
              const drivers = response.data.data as { id: string }[];
              const driverId = drivers?.[0]?.id;
              if (driverId) {
                const { failed } = await createStagedDocs(
                  CompliantType.USER,
                  driverId,
                  stagedComplianceDocs,
                );
                if (failed > 0) {
                  toasterStore.push(
                    alert(
                      t('error_messages.compliance_document_types.partial_save_failure'),
                      AlertTypes.warn,
                    ),
                  );
                }
              }
            } catch (error) {
              console.error('Failed to attach compliance documents to driver', error);
              toasterStore.push(
                alert(
                  t('error_messages.compliance_document_types.partial_save_failure'),
                  AlertTypes.warn,
                ),
              );
            }
          }
        }

        setIsDriverDialogOpen(false);
        setStagedComplianceDocs([]);
        const name = `${data?.firstName} ${data?.lastName}`;
        toasterStore.push(alert(t('user.user_created', { name }), AlertTypes.success));
        void goToDriversPage();
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          const e = error as AxiosError<ModelError_Response>;
          setAsyncErrors(e.response?.data.error.errors || []);
        } else {
          connection.handleRequestError(
            error,
            t('error_messages.drivers.failed_to_create_driver'),
          );
        }
      }
    };

    const updateDriverAccount = async (
      accountName?: string,
      accountExternalId?: string,
    ) => {
      if (!selectedDriver || !selectedDriver.companyShare?.receiverAccount) {
        return;
      }

      try {
        await patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdAccountsId({
          path: {
            'company-id': companyId,
            'managed-company-id': managedCompanyId,
            id: selectedDriver.companyShare.receiverAccount.id,
          },
          body: {
            name: accountName,
            external_id: accountExternalId,
          },
        });
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.account.failed_to_update'),
        );
      }
    };

    const onSubmitEditForm = async (userData: UserValidationDTOWithSitesDiff) => {
      if (!selectedDriver) {
        return;
      }

      setAsyncErrors([]);

      const isSharedDriver = Boolean(selectedDriver.companyShare?.id);
      const { additionalEquipment: _discardedAE, ...userDataWithoutAE } = userData;

      try {
        await updateUser(
          {
            id: selectedDriver.id,
            ...(isSharedDriver ? userDataWithoutAE : userData),
          } as unknown as User,
          undefined,
          companyId,
          selectedDriver.company?.id,
        );

        const companySharesPresentOnSubmit = userData.companyShares?.filter(
          (submittedCompanyShare) => !!submittedCompanyShare.receiverCompany.id,
        );

        // Handle company shares
        const companySharesToCreate = companySharesPresentOnSubmit?.filter(
          (submittedCompanyShare) =>
            !companyShares.some(
              (existingCompanyShare) =>
                existingCompanyShare.receiverCompany.id ===
                submittedCompanyShare.receiverCompany.id,
            ),
        );

        const companySharesToUpdate = companySharesPresentOnSubmit?.filter(
          (submittedCompanyShare) => submittedCompanyShare.id,
        );

        const companySharesToDelete = companyShares.filter(
          (existingCompanyShare) =>
            companySharesPresentOnSubmit &&
            !companySharesPresentOnSubmit.some(
              (submittedCompanyShare) =>
                existingCompanyShare.receiverCompany.id ===
                submittedCompanyShare.receiverCompany.id,
            ),
        );

        if (companySharesToCreate && companySharesToCreate.length) {
          await Promise.all(
            companySharesToCreate.map((companyShare) =>
              createCompanyShareByCompanyId({
                companyId,
                managedCompanyId,
                company_shareable_id: selectedDriver.id,
                receiver_company_id: companyShare.receiverCompany.id as string,
                company_shareable_type: 'User' as CompanyShareableType,
                rate_override: companyShare.rateOverride ?? false,
              }),
            ),
          );
        }

        if (companySharesToUpdate && companySharesToUpdate.length) {
          await Promise.all(
            companySharesToUpdate.map((companyShare) =>
              updateCompanyShare(
                {
                  companyShareId: companyShare.id as string,
                  rate_override: companyShare.rateOverride ?? false,
                },
                companyId,
                managedCompanyId,
              ),
            ),
          );
        }

        if (companySharesToDelete && companySharesToDelete.length) {
          await Promise.all(
            companySharesToDelete.map((companyShare) =>
              deleteCompanyShare(companyShare.id as string, companyId, managedCompanyId),
            ),
          );
        }

        if (selectedDriver?.companyShare) {
          await updateCompanyShare(
            {
              companyShareId: selectedDriver.companyShare.id,
              default_equipment_id: userData.defaultDriverEquipment?.id ?? null,
            },
            companyId,
            managedCompanyId,
          );

          if (showDriverTrailerSettingEnabled) {
            await patchV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdCompanySharesId(
              {
                path: {
                  'company-id': companyId,
                  'managed-company-id': managedCompanyId,
                  id: selectedDriver.companyShare.id,
                },
                body: {
                  default_additional_equipment_ids: userData
                    .defaultDriverAdditionalEquipment?.id
                    ? [userData.defaultDriverAdditionalEquipment.id]
                    : [],
                },
              },
            );
          }
        }

        if (selectedDriver?.companyShare?.receiverAccount) {
          await updateDriverAccount(userData.accountName, userData.accountExternalId);
        }

        toasterStore.push(
          alert(
            t('user.user_updated', {
              name: `${userData.firstName} ${userData.lastName}`,
            }),
            AlertTypes.success,
          ),
        );
        handleCloseEditDialog();
        void reloadDriversPage();
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          const e = error as AxiosError<ModelError_Response>;
          setAsyncErrors(e.response?.data.error.errors || []);
        } else {
          connection.handleRequestError(
            error,
            t('error_messages.drivers.failed_to_update'),
          );
        }
      }
    };

    const handleUpdateCompanyShareWithVendorCompanyId = async (
      params: Parameters<typeof updateCompanyShare>[0],
    ) => {
      return await updateCompanyShare(params, companyId, managedCompanyId);
    };

    const handleUpdateDriverWithManagedCompanyId = async (driver: Driver) => {
      try {
        return await updateUser(
          driver as unknown as User,
          undefined,
          companyId,
          driver.company?.id,
        );
      } catch (error) {
        if (isAxiosError(error) && error.response?.status === 422) {
          throw error;
        }

        // Error toast is handled in the hook.
        return undefined;
      }
    };

    const handleDeleteCompanyShare = async (companyShareId: string) => {
      try {
        await deleteCompanyShare(companyShareId, companyId, managedCompanyId);
        toasterStore.push(
          alert(t('administration.drivers.share_removed'), AlertTypes.success),
        );
        void reloadDriversPage();
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.company_shares.failed_to_unshare'),
        );
      }
    };

    const resendInvite = (
      rowId: string,
      method: 'email' | 'sms',
      companyId: string,
      managedCompanyId: string,
    ) => {
      if (rowId) {
        void sendInvitation(rowId, method, companyId, managedCompanyId)
          .then(() => {
            toasterStore.push(alert(t('user.invitation_sent'), AlertTypes.success));
          })
          .catch(() => {
            // Error toast is handled in the hook.
          });
      }
    };

    const handleDeleteUser = async () => {
      if (!userIdToDelete) {
        return;
      }

      try {
        await deleteV1CompaniesCompanyIdManagedCompaniesManagedCompanyIdUsersId({
          path: {
            'company-id': companyId,
            'managed-company-id': managedCompanyId,
            id: userIdToDelete,
          },
        });

        const message = t('administration.drivers.driver_deactivated');
        toasterStore.push(alert(message, AlertTypes.success));
        void reloadDriversPage();
      } catch (error) {
        connection.handleRequestError(
          error,
          t('error_messages.drivers.failed_to_deactivate'),
        );
      } finally {
        setUserIdToDelete(undefined);
      }
    };

    const columns = useMemo<Array<DataTableColumn<Driver>>>(
      () => [
        {
          field: 'lastName',
          headerName: t('form_fields.last_name'),
          renderCell: (driver) => (
            <div title={driver.lastName} role={'presentation'} data-sentry-mask>
              {driver.lastName}
            </div>
          ),
        },
        {
          field: 'firstName',
          headerName: t('form_fields.first_name'),
          renderCell: (driver) => (
            <div title={driver.firstName} role={'presentation'} data-sentry-mask>
              {driver.firstName}
            </div>
          ),
        },
        {
          field: 'companyType',
          headerName: t('form_fields.type'),
          valueGetter: (driver) => startCase(driver.company?.companyType || 'User'),
        },
        {
          field: 'roles',
          headerName: t('form_fields.equipment'),
          renderCell: (driver) => (
            <EquipmentManagementColumn
              driver={driver}
              canEdit={userPermissions.canEditUser}
              isUpdating={isUpdating}
              handlers={{
                onUpdateDriver: handleUpdateDriverWithManagedCompanyId,
                onUpdateCompanyShare: handleUpdateCompanyShareWithVendorCompanyId,
              }}
              companyId={companyId}
              managedCompanyId={managedCompanyId}
              showEditButton={false}
            />
          ),
        },
        ...(showDriverTrailerSettingEnabled
          ? [
              {
                field: 'additionalEquipment',
                headerName: t('form_fields.trailer'),
                renderCell: (driver: Driver) => {
                  const trailerEquipment =
                    driver.companyShare?.defaultAdditionalEquipment?.[0] || null;

                  return (
                    <Box
                      sx={{
                        display: 'flex' as const,
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        width: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          flex: 3,
                          minWidth: 200,
                        }}
                      >
                        {driver.companyShare && (
                          <EquipmentSelector
                            defaultEquipment={trailerEquipment}
                            disabled={!userPermissions.canEditUser || isUpdating}
                            onSelect={(equipment: Nullable<EquipmentTypeahead>) => {
                              if (equipment) {
                                driver.companyShare!.defaultAdditionalEquipment = [
                                  equipment as unknown as BasicEquipmentDefault,
                                ];
                                handleUpdateCompanyShareWithVendorCompanyId({
                                  companyShareId: driver.companyShare!.id,
                                  default_additional_equipment_ids: [equipment.id],
                                });
                              }
                            }}
                            clearable={false}
                            companyId={companyId}
                            managedCompanyId={managedCompanyId}
                            placeholder={t('form_fields.select_trailer')}
                          />
                        )}
                      </Box>
                      <Box
                        sx={{
                          flex: 1,
                          minWidth: 100,
                        }}
                      >
                        {trailerEquipment && (
                          <Tooltip title={t('administration.equipment.clear_trailer')}>
                            <IconButton
                              disabled={!userPermissions.canEditUser || isUpdating}
                              onClick={async () => {
                                if (driver.companyShare) {
                                  driver.companyShare.defaultAdditionalEquipment = [];
                                  await handleUpdateCompanyShareWithVendorCompanyId({
                                    companyShareId: driver.companyShare.id,
                                    default_additional_equipment_ids: [],
                                  });
                                }
                              }}
                            >
                              <Clear />
                            </IconButton>
                          </Tooltip>
                        )}
                      </Box>
                    </Box>
                  );
                },
              },
            ]
          : []),
        {
          field: 'externalId',
          headerName: t('form_fields.external_id'),
          valueGetter: (driver) => driver.externalId || '',
        },
        {
          field: 'email',
          headerName: t('form_fields.email'),
          renderCell: (driver) => (
            <div title={driver.email ?? ''} role={'presentation'} data-sentry-mask>
              {driver.email}
            </div>
          ),
        },
        {
          field: 'phone',
          headerName: t('form_fields.phone'),
          renderCell: (driver) => {
            const formatted = formatPhoneForDisplay(driver.phone);

            return (
              <div title={formatted} role={'presentation'} data-sentry-mask>
                {formatted}
              </div>
            );
          },
        },
        {
          field: 'timeZone',
          headerName: t('form_fields.time_zone'),
          valueGetter: (driver) => driver.timeZone || '',
        },
        {
          field: 'createdAt',
          headerName: t('common.created_on'),
          renderCell: (driver) => (
            <Tooltip
              title={`${t('common.last_update')}: ${driver.updatedAt ? dateFormat(driver.updatedAt) : ''}`}
            >
              <Typography variant="body2">
                {driver.createdAt ? dateFormat(driver.createdAt) : ''}
              </Typography>
            </Tooltip>
          ),
        },
        actionsColumn((driver) => {
          const hasCompanyShare = !!driver.companyShare;
          const options = [];

          // resend email invitation (for drivers who haven't activated via email)
          if (!driver.isActiveMember && driver.email) {
            options.push({
              title: t('user.resend_email_invitation'),
              icon: <Mail />,
              callBack: () =>
                resendInvite(driver.id, 'email', companyId, driver.company?.id),
            });
          }

          // resend SMS invitation (for drivers who haven't activated via SMS)
          if (!driver.isActiveUser && driver.phone) {
            options.push({
              title: t('user.resend_sms_invitation'),
              icon: <Sms />,
              callBack: () =>
                resendInvite(driver.id, 'sms', companyId, driver.company?.id),
            });
          }

          // deactivate or remove share
          if (userPermissions.canDeleteUser) {
            if (hasCompanyShare && driver.companyShare?.id) {
              // for drivers shared to the vendor -> Remove Share
              options.push({
                title: t('actions.remove_share'),
                icon: <ToggleOffOutlined />,
                callBack: () => {
                  handleDeleteCompanyShare(driver.companyShare!.id);
                },
              });
            } else {
              // for vendor's internal drivers -> deactivate
              options.push({
                title: t('actions.deactivate'),
                icon: <ToggleOffOutlined />,
                callBack: () => {
                  setUserIdToDelete(driver.id);
                },
              });
            }
          }

          return (
            <>
              {userPermissions.canEditUser && (
                <IconButton
                  aria-label={t('actions.edit')}
                  size="small"
                  onClick={() => handleEditDriver(driver.id)}
                >
                  <Edit />
                </IconButton>
              )}
              <SimpleMenu options={options}>
                <MoreHoriz />
              </SimpleMenu>
            </>
          );
        }),
      ],
      [
        handleEditDriver,
        handleDeleteCompanyShare,
        resendInvite,
        userPermissions,
        companyId,
        handleUpdateDriverWithManagedCompanyId,
        handleUpdateCompanyShareWithVendorCompanyId,
        isUpdating,
        showDriverTrailerSettingEnabled,
      ],
    );

    return (
      <Box>
        <Box
          sx={{
            alignItems: 'center',
            display: 'flex',
            justifyContent: 'space-between',
            mb: 2,
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            {t('administration.managed_companies_v2.manage_drivers_for')}
            {managedCompany.legalName}
          </Typography>
          <SmallButton
            color="brandV2Yellow"
            startIcon={<Add />}
            onClick={handleOpenDriverDialog}
          >
            {t('actions.add_driver')}
          </SmallButton>
        </Box>
        <DataTable
          id="vendor-drivers-grid"
          columns={columns}
          rows={drivers}
          loading={isFetchingDrivers}
          onSearchChange={setSearchQuery}
          headerTrailingComponent={
            <DataGridPager
              isFetching={isFetchingDrivers}
              onNavigate={(link) => void goToDriversPage(link)}
              pagination={driversPagination}
            />
          }
        />
        <DriverCreateDialog
          isOpen={isDriverDialogOpen}
          onClose={handleCloseDriverDialog}
          onSubmit={onDriverSubmitCallback}
          isManagedDriver={true}
          isLoading={isLoading}
          asyncErrors={asyncErrors}
          companyId={companyId}
          managedCompanyId={managedCompanyId}
          onStagedDocsChange={setStagedComplianceDocs}
        />
        <DriverEditDialog
          isOpen={isEditDialogOpen}
          onClose={handleCloseEditDialog}
          onSubmit={onSubmitEditForm}
          driver={selectedDriver}
          companyShares={companyShares}
          asyncErrors={asyncErrors}
          isUpdating={isUpdating}
          companyId={companyId}
          managedCompanyId={managedCompanyId}
        />
        <DeactivateConfirmationDialog
          id={userIdToDelete}
          onClose={() => setUserIdToDelete(undefined)}
          onConfirm={handleDeleteUser}
          title={t('administration.drivers.deactivate_driver_dialog_title')}
          message={t('administration.drivers.deactivate_driver_dialog_message')}
        />
      </Box>
    );
  },
);
